/* eslint-disable */
import { Col, Modal, Row, Spin, message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import InstitutionUserService from '../../services/InstitutionUserService';

interface AuditLog {
  action: string;
  created_at: string;
  created_by: string;
  record_id: string;
  log_id?: string;
  table_name?: string;
}

interface RefundRequest {
  auditLogs?: AuditLog[];
  refund_request_id?: string;
  record_id?: string;
}

const ViewToPublicTreasuryRequest = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const { state } = useLocation();
  const refundRequest = state || {};
  const bailiffRefundRequest = state || {};

  const toggleFooterDetails = () => {
    setIsFooterVisible((prevState) => !prevState);
  };

  const toggleModalVisibility = () => {
    setIsModalVisible(!isModalVisible);
    if (!isModalVisible) {
      fetchAuditLogs();
    }
  };

  const fetchAuditLogs = async () => {
    const refundRequestId = refundRequest?.refund_request_id;
    if (!refundRequestId) {
      message.warning('No refund request ID found to fetch logs');
      return;
    }

    setLoadingLogs(true);
    try {
      const response = await InstitutionUserService.getAuditLogsByRefundRequestId(refundRequestId, 100, 1);
      if (response.status === 200 && response.data) {
        setAuditLogs(response.data);
      } else {
        message.error('Failed to fetch audit logs');
        setAuditLogs([]);
      }
    } catch (error: any) {
      console.error('Error fetching audit logs:', error);
      message.error(error?.message || 'Failed to fetch audit logs');
      setAuditLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const formatDate = ({ dateString }: { dateString: any }) => {
    if (!dateString) return 'N/A'; // Safely handle empty or null date

    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <div className="w-full p-body">
      <div className="px-5 py-2.5 border-b-slate-200 border border-solid">
        <div className="flex justify-between items-center">
          <div
            className="text-title font-bold flex items-center"
            style={{
              color: '#37762f',
              fontSize: '1rem',
            }}
          >
        <span
          className="cursor-pointer flex items-center"
          onClick={() => {
            navigate('/refund-request-list');
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 24 24"
            stroke="none"
            className="w-6 h-6 mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
          Refund Request
        </span>
            {'  >  '}
            <span className="font-normal">{refundRequest?.beneficiary_account_name}</span>
          </div>
        </div>
      </div>

      <div className="px-7 py-4">
        {/* Show/Hide Logs Button */}
        <div
          className={`cursor-pointer text-white px-6 py-3 rounded-full transition-all duration-300 ${
            isModalVisible ? 'shadow-lg transform scale-105' : 'hover:bg-green-400'
          }`}
          style={{ backgroundColor: isModalVisible ? '#37762f' : '#6b8e3d' }} // #37762f for active, #6b8e3d for hover
          onClick={toggleModalVisibility}
        >
          {isModalVisible ? 'Close Logs' : 'Show Logs'}
        </div>

        {/* Refund Request Details */}
        <div className="px-7 py-4">
          <div className="bg-slate-100 p-4 rounded-lg shadow-lg">
            <div className="green-color font-extrabold text-base flex items-center leading-10 mb-4">
              <i className="fas fa-info-circle text-green-500 mr-2"></i>
              Beneficiary Refund Request Details
            </div>

            {/* Beneficiary Details */}
            <Row gutter={[16, 16]}>
              <Col xs={24} xl={8}>
                <div className="text-base font-bold text-gray-700 mb-2">Beneficiary Account Name</div>
                <div className="text-base font-medium text-gray-900">{refundRequest?.beneficiary_account_name}</div>
              </Col>
              <Col xs={24} xl={8}>
                <div className="text-base font-bold text-gray-700 mb-2">Beneficiary Bank Name</div>
                <div className="text-base font-medium text-gray-900">{refundRequest?.beneficiary_bank_name}</div>
              </Col>
              <Col xs={24} xl={8}>
                <div className="text-base font-bold text-gray-700 mb-2">Beneficiary Account Number</div>
                <div className="text-base font-medium text-gray-900">{refundRequest?.beneficiary_account_number}</div>
              </Col>
            </Row>

            <div className="pt-4">
              <Row gutter={16}>
                <Col xs={24} xl={8}>
                  <div className="text-base font-bold text-gray-700 mb-2">Debit Account Number</div>
                  <div className="text-base font-medium text-gray-900">{refundRequest?.debit_account_number}</div>
                </Col>
                <Col xs={24} xl={8}>
                  <div className="text-base font-bold text-gray-700 mb-2">Debit Account Name</div>
                  <div className="text-base font-medium text-gray-900">{refundRequest?.debit_account_name}</div>
                </Col>
                <Col xs={24} xl={8}>
                  <div className="text-base font-bold text-gray-700 mb-2">Transaction Reference</div>
                  <div className="text-base font-medium text-gray-900">{refundRequest?.transaction_reference}</div>
                </Col>
              </Row>
            </div>

            <div className="pt-4">
              <Row gutter={16}>
                <Col xs={24} xl={8}>
                  <div className="text-base font-bold text-gray-700 mb-2">Amount to be refunded</div>
                  <div className="text-base font-medium text-gray-900">
                    {parseInt(String(refundRequest?.amount)).toLocaleString()}
                  </div>
                </Col>
                <Col xs={24} xl={8}>
                  <div className="text-base font-bold text-gray-700 mb-2">Transaction Currency</div>
                  <div className="text-base font-medium text-gray-900">{refundRequest?.transaction_currency}</div>
                </Col>
                <Col xs={24} xl={8}>
                  <div className="text-base font-bold text-gray-700 mb-2">Reason</div>
                  <div className="text-base font-medium text-gray-900">{refundRequest?.reason}</div>
                </Col>
              </Row>
            </div>

            <div className="pt-4">
              <Row gutter={16}>
                <Col xs={24} xl={8}>
                  <div className="custom-col-container">
                    <div className="custom-title text-base font-bold text-gray-700">Status</div>
                    <div className="custom-status text-base font-medium text-gray-900">{refundRequest?.status}</div>
                  </div>
                </Col>
                <Col xs={24} xl={8}>
                  <div className="text-base font-bold text-gray-700 mb-2">Requested Date</div>
                  <div className="text-base font-medium text-gray-900">
                    {formatDate({ dateString: refundRequest?.created_at })}
                  </div>
                </Col>
                <Col xs={24} xl={8}>
                  <div className="text-base font-bold text-gray-700 mb-2">Updated Date</div>
                  <div className="text-base font-medium text-gray-900">
                    {formatDate({ dateString: refundRequest?.updated_at })}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </div>

        {/* Modal to display logs */}
        <Modal
          visible={isModalVisible}
          onCancel={toggleModalVisibility}
          footer={null}
          width="60%"
          centered
          title={
            <div
              style={{
                backgroundColor: '#37762f',
                color: 'white',
                fontSize: 'var(--base-font-size)',
                padding: '10px',
                fontWeight: 'bold',
                borderRadius: '5px',
              }}
            >
              <i className="fas fa-history mr-2"></i>
              Refund Request Logs
            </div>
          }
        >
          <div className="footer-details mt-4 p-4 bg-slate-100 rounded-lg">
            <div className="text-large font-extrabold text-green-600 leading-10 mb-4">
              Refund Request Logs
            </div>

            {/* Created Logs */}
            <div className="pt-4">
              <div className="text-title font-semibold text-gray-800 mb-4">Created Logs</div>

              {loadingLogs ? (
                <div className="text-center py-8">
                  <Spin size="large" />
                </div>
              ) : auditLogs && auditLogs.length > 0 ? (
                auditLogs.map((log: AuditLog, index: number) => (
                  <div
                    key={log.log_id || index}
                    className="mb-4 p-4"
                    style={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <Row gutter={[16, 16]}>
                  <Col xs={24} xl={6}>
                        <div className="log-label text-base font-bold text-gray-700 mb-2">Action:</div>
                        <div className="log-content text-base font-medium text-gray-900 p-2 bg-gray-50 rounded">{log?.action}</div>
                  </Col>
                  <Col xs={24} xl={6}>
                        <div className="log-label text-base font-bold text-gray-700 mb-2">Created By:</div>
                        <div className="log-content text-base font-medium text-gray-900 p-2 bg-gray-50 rounded">{log?.created_by || 'N/A'}</div>
                  </Col>
                  <Col xs={24} xl={6}>
                        <div className="log-label text-base font-bold text-gray-700 mb-2">Created At:</div>
                        <div className="log-content text-base font-medium text-gray-900 p-2 bg-gray-50 rounded">
                      {formatDate({ dateString: log?.created_at })}
                    </div>
                  </Col>
                  <Col xs={24} xl={6}>
                        <div className="log-label text-base font-bold text-gray-700 mb-2">Table Name:</div>
                        <div className="log-content text-base font-medium text-gray-900 p-2 bg-gray-50 rounded">{log?.table_name || 'N/A'}</div>
                  </Col>
                </Row>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="text-base font-medium text-gray-500">No audit logs found</div>
                </div>
              )}
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ViewToPublicTreasuryRequest;
